import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@material-ui/core';

/**
 * Le texte d'une ligne d'activité, rendu sans jamais injecter de HTML.
 *
 * La page Activités affichait `description` par `dangerouslySetInnerHTML`.
 * Or cette colonne est écrite par une centaine d'endroits du CRM, dont deux
 * seulement échappent ce qu'ils y mettent — et l'un des canaux d'écriture est
 * l'analyse de courriels entrants, donc non authentifié. Il suffisait d'un
 * `<img src=x onerror=…>` posé dans un champ libre pour que le code parte
 * dans le navigateur du premier administrateur ouvrant la page.
 *
 * Plutôt que d'ajouter un assainisseur — une dépendance de plus, et une liste
 * d'exceptions à tenir — on ne rend plus de HTML du tout. On reconnaît les
 * deux balises que le CRM produit légitimement, le lien et la mise en gras,
 * et React se charge du reste : tout ce qui n'est pas reconnu devient du
 * texte visible. Une tentative d'injection s'affiche alors en clair, ce qui
 * la rend inoffensive et repérable.
 */

/** Liens, gras — rien d'autre n'est interprété. */
const MOTIF = /<a\s+[^>]*href=['"]([^'"]*)['"][^>]*>(.*?)<\/a>|<(?:strong|b)>(.*?)<\/(?:strong|b)>/gi;

/**
 * Une adresse est-elle sûre à suivre ?
 *
 * Seuls les chemins internes sont admis. `javascript:`, `data:` et les
 * domaines extérieurs sont refusés : le lien devient alors du texte.
 *
 * @param {string} href
 * @returns {boolean}
 */
const interne = (href) => typeof href === 'string'
  && href.startsWith('/')
  && !href.startsWith('//');

/** Les entités que le CRM écrit dans ses descriptions. */
const detexte = (t) => String(t || '')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&#0?39;/g, '\'')
  .replace(/&nbsp;/g, ' ');

const DescriptionLog = ({ texte }) => {
  const source = String(texte || '');
  const morceaux = [];
  let curseur = 0;
  let m = MOTIF.exec(source);
  let i = 0;

  while (m !== null) {
    if (m.index > curseur) {
      morceaux.push(detexte(source.slice(curseur, m.index)));
    }

    if (m[3] !== undefined) {
      // Mise en gras.
      morceaux.push(
        // eslint-disable-next-line react/no-array-index-key
        <strong key={`b${i}`}>{detexte(m[3])}</strong>
      );
    } else if (interne(m[1])) {
      morceaux.push(
        <RouterLink
          // eslint-disable-next-line react/no-array-index-key
          key={`a${i}`}
          to={m[1]}
          style={{ color: 'inherit', fontWeight: 700 }}
        >
          {detexte(m[2])}
        </RouterLink>
      );
    } else {
      // Lien vers l'extérieur ou adresse douteuse : on garde le libellé,
      // on jette la destination.
      morceaux.push(detexte(m[2]));
    }

    curseur = m.index + m[0].length;
    i += 1;
    m = MOTIF.exec(source);
  }

  if (curseur < source.length) {
    morceaux.push(detexte(source.slice(curseur)));
  }

  return <Box component="span">{morceaux}</Box>;
};

export default DescriptionLog;
